import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/Kpi";

const inp = "border border-slate-200 rounded-md px-3 py-2 text-sm bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-slate-300 transition-colors duration-200";
const lbl = "text-xs uppercase tracking-[0.16em] text-slate-400 mb-1 block";

/** Gestione della struttura del menu: tendine (gruppi) e pagine create dall'utente. */
export function StrutturaMenu() {
  const [voci, setVoci] = useState([]);
  const [grafici, setGrafici] = useState([]);
  const [nuovo, setNuovo] = useState({ tipo: "pagina", nome: "", parent: "impostazioni",
    ordine: 100, per_commerciale: false });

  const carica = () => api.get("/menu-struttura")
    .then((r) => {
      setVoci(r.data.voci || []);
      window.dispatchEvent(new CustomEvent("menu-struttura-changed"));
    }).catch(() => setVoci([]));

  useEffect(() => {
    carica();
    api.get("/grafici").then((r) => setGrafici(r.data.grafici || [])).catch(() => setGrafici([]));
  }, []);

  const gruppi = voci.filter((v) => v.tipo === "gruppo");
  const pagine = voci.filter((v) => v.tipo === "pagina");

  const crea = async () => {
    try {
      await api.post("/menu-struttura", nuovo);
      setNuovo({ ...nuovo, nome: "" });
      await carica();
      toast.success(nuovo.tipo === "gruppo" ? "Tendina creata" : "Pagina creata");
    } catch (e) { toast.error(apiError(e)); }
  };

  const salva = async (v, upd) => {
    try {
      await api.put(`/menu-struttura/${v.id}`, upd);
      await carica();
      toast.success("Struttura aggiornata");
    } catch (e) { toast.error(apiError(e)); }
  };

  const elimina = async (v) => {
    try {
      await api.delete(`/menu-struttura/${v.id}`);
      await carica();
      toast.success("Voce eliminata");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Card className="p-6" data-testid="config-struttura-menu">
      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
        Struttura del menu
      </h3>
      <p className="text-sm text-slate-500 mb-5">
        Crea nuove tendine e nuove pagine: appaiono subito nel menu laterale.
        Le pagine nascono vuote e possono mostrare i grafici salvati in Formule e grafici.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div>
          <label className={lbl}>Tipo</label>
          <select className={`${inp} w-full`} value={nuovo.tipo} data-testid="menu-nuovo-tipo"
            onChange={(e) => setNuovo({ ...nuovo, tipo: e.target.value })}>
            <option value="pagina">Pagina</option>
            <option value="gruppo">Tendina</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={lbl}>Nome</label>
          <input className={`${inp} w-full`} value={nuovo.nome} data-testid="menu-nuovo-nome"
            placeholder={nuovo.tipo === "gruppo" ? "es. Analisi" : "es. Report mensile"}
            onChange={(e) => setNuovo({ ...nuovo, nome: e.target.value })} />
        </div>
        {nuovo.tipo === "pagina" && (
          <div>
            <label className={lbl}>Dentro</label>
            <select className={`${inp} w-full`} value={nuovo.parent} data-testid="menu-nuovo-parent"
              onChange={(e) => setNuovo({ ...nuovo, parent: e.target.value })}>
              <option value="home">Home</option>
              <option value="impostazioni">Impostazioni</option>
              {gruppi.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
            </select>
          </div>
        )}
        {nuovo.tipo === "pagina" && nuovo.parent === "home" && (
          <div className="md:col-span-5">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
              <input type="checkbox" checked={nuovo.per_commerciale} data-testid="menu-nuovo-per-commerciale"
                onChange={(e) => setNuovo({ ...nuovo, per_commerciale: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400" />
              Mostrala anche sotto ogni commerciale (con i filtri del commerciale)
            </label>
          </div>
        )}
        <div>
          <button onClick={crea} disabled={!nuovo.nome.trim()} data-testid="menu-crea"
            className="w-full bg-slate-900 text-white rounded-md px-4 py-2 text-sm
              hover:bg-slate-800 disabled:opacity-40 transition-colors duration-200">
            Aggiungi
          </button>
        </div>
      </div>

      {(gruppi.length > 0 || pagine.length > 0) && (
        <div className="mt-6 space-y-2" data-testid="menu-voci">
          {[...gruppi, ...pagine].map((v) => (
            <div key={v.id} data-testid={`menu-voce-${v.slug}`}
              className="flex flex-wrap items-center gap-2 border border-slate-200 rounded-md px-3 py-2">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full
                ${v.tipo === "gruppo" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-600"}`}>
                {v.tipo}
              </span>
              <input className={`${inp} min-w-[200px]`} defaultValue={v.nome}
                data-testid={`menu-nome-${v.slug}`}
                onBlur={(e) => e.target.value !== v.nome && salva(v, { nome: e.target.value })} />
              {v.tipo === "pagina" && (
                <>
                  <select className={inp} value={v.parent || "impostazioni"}
                    data-testid={`menu-parent-${v.slug}`}
                    onChange={(e) => salva(v, { parent: e.target.value })}>
                    <option value="home">Home</option>
                    <option value="impostazioni">Impostazioni</option>
                    {gruppi.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
                  </select>
                  <select className={inp} value={(v.grafici || [])[0] || ""}
                    data-testid={`menu-grafico-${v.slug}`}
                    onChange={(e) => salva(v, { grafici: e.target.value ? [e.target.value] : [] })}>
                    <option value="">Nessun grafico</option>
                    {grafici.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
                  </select>
                </>
              )}
              {v.tipo === "pagina" && (v.parent || "impostazioni") === "home" && (
                <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={!!v.per_commerciale}
                    data-testid={`menu-per-commerciale-${v.slug}`}
                    onChange={(e) => salva(v, { per_commerciale: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-sky-600" />
                  anche per commerciale
                </label>
              )}
              <input type="number" className={`${inp} w-24`} defaultValue={v.ordine}
                data-testid={`menu-ordine-${v.slug}`}
                onBlur={(e) => Number(e.target.value) !== v.ordine && salva(v, { ordine: Number(e.target.value) })} />
              <span className="font-mono text-xs text-slate-400">/{v.slug}</span>
              <button onClick={() => elimina(v)} data-testid={`menu-elimina-${v.slug}`}
                className="ml-auto text-slate-400 hover:text-red-600 transition-colors duration-150">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
